'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useRetailStoreLocator } from 'hooks/navigation/useRetailStoreLocator';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { bindOnceTextReveal, TEXT_REVEAL_FULL } from './scrollBeat';
import {
  EARTH_GLOBE_OVERLAY_SRC,
  EARTH_GLOBE_SNAPSHOT_SRC,
  prefetchEarthGlobeTextures,
  shouldUseEarthGlobeWebGL,
} from './worldSleep/earthGlobeAssets';
import type {
  WorldSleepGlobeCardContent,
  WorldSleepHoverHit,
} from './worldSleep/worldSleepFormat';
import { useLiveNightsTick } from './worldSleep/useLiveNightsTick';
import v3 from './v3.module.css';
import styles from './WorldSleepSection.module.css';

/** three.js + the globe engine stay out of the homepage bundle until it scrolls near. */
const WorldSleepGlobeCanvas = dynamic(
  () => import('./worldSleep/WorldSleepGlobeCanvas'),
  { ssr: false },
);

function useGlobeLoad(ref: RefObject<HTMLElement | null>) {
  const [mountGlobe, setMountGlobe] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!shouldUseEarthGlobeWebGL()) return;

    let prefetched = false;
    const prefetch = () => {
      if (prefetched) return;
      prefetched = true;
      prefetchEarthGlobeTextures();
      void import('./worldSleep/WorldSleepGlobeCanvas');
    };

    const prefetchIo = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          prefetch();
          prefetchIo.disconnect();
        }
      },
      { rootMargin: '100% 0px' },
    );

    const mountIo = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          prefetch();
          setMountGlobe(true);
          mountIo.disconnect();
        }
      },
      { rootMargin: '20% 0px' },
    );

    prefetchIo.observe(el);
    mountIo.observe(el);
    return () => {
      prefetchIo.disconnect();
      mountIo.disconnect();
    };
  }, [ref]);

  return mountGlobe;
}

export const WorldSleepSection = ({
  nights,
  nightsPer5m,
  idleCards,
  exploreCards,
  hoverCountries,
  hoverHits,
  layout,
  'aria-label': ariaLabel,
}: {
  nights: number;
  nightsPer5m: number;
  idleCards: WorldSleepGlobeCardContent[];
  exploreCards: WorldSleepGlobeCardContent[];
  hoverCountries: WorldSleepGlobeCardContent[];
  hoverHits: WorldSleepHoverHit[];
  /** Desktop split with the globe on the left. Default keeps copy on the left. */
  layout?: 'split';
  'aria-label'?: string;
}) => {
  const t = useTranslations('homepage');
  const { hasStores, openStoreLocator } = useRetailStoreLocator('hero');
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const statsRef = useRef<HTMLDListElement>(null);
  const nightsCountRef = useRef<HTMLSpanElement>(null);
  const mountGlobe = useGlobeLoad(sectionRef);
  const gsapCtx = useGsapContext(sectionRef);
  const [globeReady, setGlobeReady] = useState(false);
  const [skipWebGL, setSkipWebGL] = useState(false);

  useLiveNightsTick(nightsCountRef, statsRef, nights, nightsPer5m);

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const title = titleRef.current;
      const unbindTitle = title
        ? bindOnceTextReveal(title, { ...TEXT_REVEAL_FULL, trigger: title })
        : () => undefined;

      return () => {
        unbindTitle();
      };
    });
  }, [gsapCtx]);

  const titleId = 'world-sleep-title';
  const nightsLabel = nights.toLocaleString('en-US');
  const snapshotDecorative = globeReady || (mountGlobe && !skipWebGL);

  return (
    <section
      ref={sectionRef}
      className={
        layout === 'split' ? `${styles.outer} ${styles.split}` : styles.outer
      }
      aria-labelledby={titleId}
      aria-label={ariaLabel ?? t('homepage.worldSleep.aria.section')}
      data-header-theme="light"
    >
      <div className={styles.inner}>
        <div className={styles.copyColumn}>
          <div className={styles.copy}>
            <h2 id={titleId} ref={titleRef} className={styles.title}>
              {t('homepage.worldSleep.title')}
            </h2>
            <p className={styles.body}>
              {layout === 'split'
                ? t('homepage.ringProUs.worldSleep.subtitle')
                : t('homepage.worldSleep.subtitle')}
            </p>
            {hasStores ? (
              <button
                type="button"
                className={`${v3.pill} ${v3.pillSecondary} ${styles.findStore}`}
                onClick={openStoreLocator}
                data-buttontype="world_sleep_find_store"
                aria-label={t('homepage.worldSleep.aria.findStore')}
              >
                {t('homepage.worldSleep.findStore')}
              </button>
            ) : null}
          </div>

          <dl
            ref={statsRef}
            className={styles.stats}
            aria-label={t('homepage.worldSleep.aria.stats')}
          >
            <div className={styles.stat}>
              <dt className={styles.statLabel}>
                {t('homepage.worldSleep.facts.nightsTracked')}
              </dt>
              <dd className={styles.statValue}>
                <span className={v3.srOnly}>{nightsLabel}</span>
                <span ref={nightsCountRef} aria-hidden="true">
                  {nightsLabel}
                </span>
              </dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.statLabel}>
                {t('homepage.worldSleep.readout.countries')}
              </dt>
              <dd className={styles.statValue}>
                {t('homepage.worldSleep.readout.countriesCount')}
              </dd>
            </div>
          </dl>
        </div>

        <div className={styles.globeStage}>
          <div className={styles.globeBleed}>
            {mountGlobe && !skipWebGL ? (
              <WorldSleepGlobeCanvas
                eager={globeReady}
                idleCards={idleCards}
                exploreCards={exploreCards}
                hoverCountries={hoverCountries}
                hoverHits={hoverHits}
                onReady={() => setGlobeReady(true)}
                onWebGLUnavailable={() => setSkipWebGL(true)}
                aria-label={t('homepage.worldSleep.aria.globe')}
              />
            ) : null}
            <div
              className={`${styles.globeMedia} ${
                globeReady ? styles.globeMediaReady : ''
              }`}
            >
              <CustomImage
                className={`${styles.globeSnapshot} ${
                  globeReady ? styles.globeSnapshotFaded : ''
                }`}
                src={EARTH_GLOBE_SNAPSHOT_SRC}
                alt={
                  snapshotDecorative
                    ? ''
                    : t('homepage.worldSleep.aria.globeSnapshot')
                }
                fill
                sizes="(min-width: 992px) 54vw, 100vw"
                aria-hidden={snapshotDecorative || undefined}
              />
            </div>
            {/* CDN SVG vignette; globeReadableRegion measures this box. */}
            <img
              className={styles.globeFade}
              src={EARTH_GLOBE_OVERLAY_SRC}
              alt=""
              aria-hidden="true"
              data-globe-overlay="true"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
